import { useEditorStore } from "@/stores/editor/editor-store";
import { useCollaborationStore } from "@/stores/collaboration/collaboration-store";
import { useResponsiveStore } from "@/stores/editor/responsive-store";
import { Component, ComponentType, Project, Viewport } from "@/types";
import type { EditorStoreState } from "@/stores/editor/editor-store";
import type { CollaborationState } from "@/stores/collaboration/collaboration-store";
import {
  EnhancedComponentContext,
  ParentContext,
  SiblingContext,
  ViewportContext,
  LayerContext,
  ContextCollectionOptions,
  CompressedComponentContext
} from "@/types/ai";
import type { BaseComponentProperties } from "@/types/component-properties";
import { logger } from "@/utils";
import { templateInstantiationService } from "./TemplateInstantiationService";
import {
  summarizeBindingContextForAi,
  type AiBindingContextComponentInput,
  type AiBindingContextSummary,
  type AiBindingSourceIdentitySummary,
  type AiBindingWriteTargetSummary,
} from "./BindingContextSummary";
import {
  buildSafeAiComponentContextFromComponent,
  sanitizeComponentPropertiesForAi,
} from "./AIComponentContextSanitizer";

type PresentAiBindingWriteTargetSummary = Extract<
  AiBindingWriteTargetSummary,
  { present: true }
>;
type FederatedAiBindingWriteTargetSummary = Extract<
  PresentAiBindingWriteTargetSummary,
  { kind: "federated-view-source" }
>;
type DirectAiBindingWriteTargetSummary = Extract<
  PresentAiBindingWriteTargetSummary,
  { target: AiBindingSourceIdentitySummary }
>;
type AiBindingWriteReadiness = PresentAiBindingWriteTargetSummary["readiness"];

function isFederatedWriteTargetSummary(
  summary: AiBindingWriteTargetSummary,
): summary is FederatedAiBindingWriteTargetSummary {
  return (
    summary.present &&
    "kind" in summary &&
    summary.kind === "federated-view-source"
  );
}

function isDirectWriteTargetSummary(
  summary: AiBindingWriteTargetSummary,
): summary is DirectAiBindingWriteTargetSummary {
  return summary.present && "target" in summary;
}

// Context data interfaces
export interface ComponentSummary {
  id: string;
  type: ComponentType;
  name?: string;
  description?: string;
  properties?: Record<string, unknown>;
  isSelected: boolean;
  position: { x: number; y: number };
  size: { width: number; height: number };
  // Template-related metadata
  templateInstanceId?: string;
  templateNodeId?: string;
  semanticRole?: string;
  isTemplateComponent?: boolean;
  // Enhanced properties for better AI understanding
  visualConfig?: {
    chartType?: string;
    labels?: string[];
    colors?: string[];
    axes?: {
      xAxis?: { label?: string; type?: string };
      yAxis?: { label?: string; type?: string; unit?: string };
    };
    series?: Array<{
      name?: string;
      type?: string;
      data?: string; // Changed to string since we're converting to "X data points"
    }>;
  };
  content?: {
    text?: string;
    title?: string;
    placeholder?: string;
    value?: unknown;
  };
  styling?: {
    theme?: string;
    colors?: string[];
    fontSize?: string;
    fontWeight?: string;
  };
  bindingContext?: AiBindingContextSummary;
}

export interface ProjectContext {
  id: string;
  name: string;
  description: string;
  componentCount: number;
  lastModified: Date;
  isAgricultural: boolean;
}

export interface CanvasContext {
  selectedComponents: ComponentSummary[];
  recentComponents: ComponentSummary[];
  allComponents: ComponentSummary[]; // All components for layout analysis
  viewport: Viewport;
  totalComponents: number;
  zoom: number;
  gridEnabled: boolean;
  currentPageId?: string;
  multiPageMode: boolean;
}

export interface UserContext {
  currentAction: "editing" | "designing" | "collaborating" | "idle";
  lastActivity: Date;
  workflowContext: string;
  isOwner: boolean;
  collaboratorCount: number;
}

export interface AIContextData {
  project: ProjectContext | null;
  canvas: CanvasContext;
  user: UserContext;
  timestamp: Date;
  contextVersion: string;
}

// Component type descriptions for AI understanding
export const COMPONENT_DESCRIPTIONS: Record<ComponentType, string> = {
  [ComponentType.TEXT]: "Text display component for labels, headers, and content",
  [ComponentType.CONTAINER]: "Layout container for organizing other components",
  [ComponentType.FLEX_CONTAINER]: "Flexible layout container with responsive design",
  [ComponentType.CARD]: "Card container for grouping related content with visual styling",
  [ComponentType.INPUT]: "Input field for user data entry",
  [ComponentType.BUTTON]: "Interactive button for user actions",
  [ComponentType.IMAGE]: "Image display component for visuals and media",
  [ComponentType.ICON]: "Icon component for visual indicators and actions",
  [ComponentType.CHART]: "Generic chart component for data visualization",
  [ComponentType.BAR_CHART]: "Bar chart for comparing categories of data",
  [ComponentType.LINE_CHART]: "Line chart for showing trends over time",
  [ComponentType.PIE_CHART]: "Pie chart for showing proportional data",
  [ComponentType.SCATTER_CHART]: "Scatter plot for correlation analysis",
  [ComponentType.AREA_CHART]: "Area chart for cumulative data visualization",
  [ComponentType.HEATMAP_CHART]: "Heatmap for density and intensity visualization",
  [ComponentType.TREEMAP_CHART]: "Treemap for hierarchical data representation",
  [ComponentType.GAUGE_CHART]: "Gauge chart for single metric visualization",
  [ComponentType.VERTICAL_GAUGE_CHART]: "Vertical gauge chart for tank levels and fill indicators",
  [ComponentType.DATA_CARD]: "Data card for displaying key metrics with labels and values",
  [ComponentType.FERMENTATION_TANK_CARD]: "Fermentation tank visualization with level and status indicators",
  [ComponentType.TABLE]: "Table component for structured data display",
  [ComponentType.ADVANCED_TABLE]: "Advanced table with filtering, sorting, and data management",
  [ComponentType.LIST]: "List component for displaying sequential data and items",
  [ComponentType.CUSTOM]: "Custom component with specialized functionality",
  [ComponentType.DATA_TEXT]: "Database-connected text component for dynamic data display",
  [ComponentType.MAP_CHART]: "Map visualization component for geographic data",
  [ComponentType.FORM_CONTAINER]: "Form container for organizing input fields and form elements",
  [ComponentType.BREADCRUMB]: "Breadcrumb navigation component for page hierarchy",
  [ComponentType.FILE_STORAGE]: "File storage component for uploading, managing, and browsing files",
  [ComponentType.KANBAN_BOARD]: "Kanban board for organizing records into columns with drag-and-drop card movement",
  [ComponentType.RADIAL_DIAL]: "Interactive radial dial control for numeric value adjustment with on/off toggle",
  [ComponentType.TOGGLE_SWITCH]: "On/off toggle switch control with customizable appearance",
  [ComponentType.PUSH_BUTTON]: "Momentary push button for triggering actions",
  [ComponentType.ICON_BUTTON]: "Icon-based button with optional label for quick actions",
  [ComponentType.STATUS_LIGHT]: "Status indicator light showing state via color and blink patterns",
  [ComponentType.SLIDER]: "Range slider control for numeric value adjustment",
  [ComponentType.STEPPER]: "Plus/minus stepper for incrementing and decrementing values",
  [ComponentType.LEVEL_INDICATOR]: "Stepped level indicator showing fill level like a battery gauge",
  [ComponentType.SEGMENTED_CONTROL]: "Multi-option segmented selector for mode or state selection",
  [ComponentType.COLOR_PICKER]: "Color selection control with wheel, strip, or palette modes",
  [ComponentType.THERMOSTAT]: "Temperature control display with mode selection and set-point adjustment",
  [ComponentType.MODAL]: "Popup overlay container triggered by click, holds child components in a dialog",
  [ComponentType.CHECKLIST]: "Interactive checklist with checkable items, progress tracking, priority indicators, assignee badges, and grouped sections",
  [ComponentType.HORIZONTAL_TIMELINE]: "Horizontal or vertical timeline showing milestones, phases, dates, and status with milestone dots or gantt bar variants",
  [ComponentType.ASSIGNMENT_WIDGET]: "Team assignment display showing avatars, names, roles, task counts, and status indicators in list, grid, or compact layouts",
  [ComponentType.GANTT_CHART]: "Gantt chart for visualizing tasks as time-scaled horizontal bars with dependencies, milestones, and progress tracking",
  [ComponentType.CALENDAR]: "Calendar component for displaying events, tasks, and scheduling with month, week, day, and agenda views",
  [ComponentType.QUESTIONNAIRE]: "Multi-step questionnaire with branching logic, per-user response collection, dynamic step generation, and aggregated results view",
  [ComponentType.VOTING_CARD]: "Voting card with approve/veto, single-choice, ranked, and thumbs vote modes. Supports real-time WebSocket vote updates, configurable resolution (unanimous/majority/quorum), deadline enforcement, and optional approval engine integration",
  [ComponentType.PROJECT_HUB]: "Project hub dashboard aggregating tasks, milestones, team activity, and project status into a unified overview",
  [ComponentType.TIME_TRACKER]: "Time tracker with running timer, manual entry, client/project/task hierarchy, configurable hourly rates, calendar view, summary and detailed reports, and PDF invoice generation for client billing",
};

// Agricultural context keywords for enhanced AI understanding
export const AGRICULTURAL_KEYWORDS = [
  "water", "irrigation", "soil", "crop", "harvest", "yield", "farm", "field",
  "sensor", "weather", "moisture", "temperature", "livestock", "cattle",
  "fertilizer", "pesticide", "growth", "season", "planting", "cultivation",
  "greenhouse", "nutrition", "pest", "disease", "monitoring", "analytics"
];

class AIContextService {
  private static instance: AIContextService;

  static getInstance(): AIContextService {
    if (!AIContextService.instance) {
      AIContextService.instance = new AIContextService();
    }
    return AIContextService.instance;
  }

  /**
   * Collect current context from all relevant stores
   */
  collectContext(): AIContextData {
    try {
      const editorState = useEditorStore.getState();
      const collaborationState = useCollaborationStore.getState();
      const projectContext = this.extractProjectContext(editorState.project);
      const canvasContext = this.extractCanvasContext(editorState);
      const userContext = this.extractUserContext(editorState, collaborationState);

      const context: AIContextData = {
        project: projectContext,
        canvas: canvasContext,
        user: userContext,
        timestamp: new Date(),
        contextVersion: "1.0.0"
      };

      logger.debug("AI context collected", {
        hasProject: projectContext !== null,
        componentCount: canvasContext.totalComponents,
        selectedCount: canvasContext.selectedComponents.length,
        userAction: userContext.currentAction
      });

      return context;
    } catch {
      logger.warn("Failed to collect AI context");
      return this.getEmptyContext();
    }
  }

  /**
   * Extract project-related context
   */
  private extractProjectContext(project: Project | null): ProjectContext | null {
    if (!project) return null;

    const isAgricultural = this.detectAgriculturalContext(
      project.name + " " + project.description
    );

    return {
      id: project.id,
      name: project.name,
      description: project.description,
      componentCount: 0, // Will be filled from canvas context
      lastModified: project.lastModifiedBy ? new Date() : new Date(),
      isAgricultural
    };
  }

  /**
   * Extract canvas and component context
   */
  private extractCanvasContext(editorState: EditorStoreState): CanvasContext {
    const components = editorState.components;
    const selectedComponentIds = editorState.selectedComponents;

    const selectedComponents = this.createComponentSummaries(
      components.filter((c: Component) => selectedComponentIds.includes(c.id)),
      true,
      components,
    );

    // Get recently modified components (last 5 modified)
    const recentComponents = this.createComponentSummaries(
      [...components] // Create a copy to avoid modifying read-only array
        .sort((a: Component, b: Component) => {
          const aTime = a.lastModifiedBy ? Date.now() : 0;
          const bTime = b.lastModifiedBy ? Date.now() : 0;
          return bTime - aTime;
        })
        .slice(0, 5)
        .filter((c: Component) => !selectedComponentIds.includes(c.id)),
      false,
      components,
    );

    // Get all components for layout requests
    const allComponents = this.createComponentSummaries(components, false, components);

    return {
      selectedComponents,
      recentComponents,
      allComponents, // Add all components for layout analysis
      viewport: editorState.viewport || Viewport.DESKTOP,
      totalComponents: components.length,
      zoom: editorState.zoom || 1,
      gridEnabled: editorState.gridEnabled || false,
      currentPageId: editorState.currentPageId,
      multiPageMode: editorState.pages.length > 1
    };
  }

  /**
   * Extract user and collaboration context
   */
  private extractUserContext(
    editorState: EditorStoreState,
    collaborationState: Pick<CollaborationState, "activeUsers">,
  ): UserContext {
    const activeUsers = collaborationState.activeUsers.size;

    // Determine current action based on state
    let currentAction: UserContext["currentAction"] = "idle";
    if (editorState.draggedComponent) {
      currentAction = "editing";
    } else if (editorState.selectedComponents.length > 0) {
      currentAction = "designing";
    } else if (activeUsers > 1) {
      currentAction = "collaborating";
    }

    // Determine workflow context based on recent actions
    const workflowContext = this.determineWorkflowContext(editorState);

    return {
      currentAction,
      lastActivity: new Date(),
      workflowContext,
      isOwner: true, // TODO: Determine based on project permissions
      collaboratorCount: Math.max(0, activeUsers - 1)
    };
  }

  /**
   * Create component summaries for AI context
   */
  private createComponentSummaries(
    components: Component[],
    isSelected: boolean,
    relatedComponents: Component[] = components,
  ): ComponentSummary[] {
    return components.map(component => {
      const props: BaseComponentProperties = component.properties;
      const templateMetadata = templateInstantiationService.getComponentMetadata(component.id);
      const bindingContext = this.extractBindingContextSummary(
        component,
        relatedComponents,
      );

      return {
        id: component.id,
        type: component.type,
        name: this.getComponentDisplayName(component),
        description: COMPONENT_DESCRIPTIONS[component.type],
        properties: this.sanitizeProperties(component.properties),
        isSelected,
        position: { x: component.position?.x ?? 0, y: component.position?.y ?? 0 },
        size: {
          width: typeof component.size?.width === 'number' ? component.size.width : 200,
          height: typeof component.size?.height === 'number' ? component.size.height : 100,
        },
        // Template metadata
        templateInstanceId: props?._templateInstanceId || templateMetadata?.templateId,
        templateNodeId: props?._templateNodeId || templateMetadata?.parentTemplateId,
        semanticRole: props?._semanticRole || templateMetadata?.semanticRole,
        isTemplateComponent: templateInstantiationService.isTemplateComponent(component.id),
        // Enhanced analysis
        visualConfig: this.extractVisualConfig(component),
        content: this.extractContent(component),
        styling: this.extractStyling(component),
        bindingContext
      };
    });
  }

  private extractBindingContextSummary(
    component: Component,
    relatedComponents: Component[],
  ): AiBindingContextSummary | undefined {
    if (!this.hasBindingContextInput(component)) {
      return undefined;
    }

    return summarizeBindingContextForAi(
      this.toBindingContextComponentInput(component),
      {
        relatedComponents: relatedComponents.map((relatedComponent) =>
          this.toBindingContextComponentInput(relatedComponent),
        ),
      },
    );
  }

  private hasBindingContextInput(component: Component): boolean {
    return component.properties.dataBinding !== undefined;
  }

  private toBindingContextComponentInput(
    component: Component,
  ): AiBindingContextComponentInput {
    return {
      id: component.id,
      type: component.type,
      name: this.getComponentDisplayName(component),
      properties: component.properties,
      metadata: component.metadata,
    };
  }

  /**
   * Get a human-readable name for a component
   */
  private getComponentDisplayName(component: Component): string {
    // Try to extract meaningful name from properties
    const props = component.properties;

    if (props.title) return String(props.title);
    if (props.text) return String(props.text).substring(0, 30);
    if (props.label) return String(props.label);
    if (props.placeholder) return String(props.placeholder);

    // Fallback to type-based name
    return `${component.type.replace('-', ' ')} component`;
  }

  /**
   * Extract visual configuration for charts and visualizations
   */
  private extractVisualConfig(component: Component): ComponentSummary['visualConfig'] | undefined {
    const props = component.properties;

    // Only process visual components
    if (!this.isVisualComponent(component.type)) {
      return undefined;
    }

    const config: ComponentSummary['visualConfig'] = {};

    // Chart type and data source
    if (props.chartType) config.chartType = String(props.chartType);
    // Labels and colors
    if (Array.isArray(props.labels)) config.labels = props.labels.map(String);
    if (Array.isArray(props.colors)) config.colors = props.colors.map(String);
    if (props.color) config.colors = [String(props.color)];

    // Axes configuration
    const axes: {
      xAxis?: { label?: string; type?: string };
      yAxis?: { label?: string; type?: string; unit?: string };
    } = {};
    if (props.xAxisLabel) axes.xAxis = { label: String(props.xAxisLabel) };
    if (props.yAxisLabel) axes.yAxis = { label: String(props.yAxisLabel) };
    if (props.yAxisUnit) {
      if (!axes.yAxis) axes.yAxis = {};
      axes.yAxis.unit = String(props.yAxisUnit);
    }
    if (Object.keys(axes).length > 0) config.axes = axes;

    // Series data (simplified for AI context)
    if (Array.isArray(props.series)) {
      config.series = props.series.map((series) =>
        this.summarizeVisualSeriesEntry(series),
      );
    }

    return Object.keys(config).length > 0 ? config : undefined;
  }

  /**
   * Extract content information from components
   */
  private extractContent(component: Component): ComponentSummary['content'] | undefined {
    const props = component.properties;
    const content: ComponentSummary['content'] = {};

    // Text content - check all possible text properties
    const textProps = ['text', 'content', 'textContent', 'innerText', 'displayText'];
    for (const prop of textProps) {
      if (props[prop]) {
        content.text = String(props[prop]);
        break;
      }
    }

    // Title and label content
    if (props.title) content.title = String(props.title);
    if (props.placeholder) content.placeholder = String(props.placeholder);
    if (props.label) content.title = String(props.label);
    if (props.value) content.value = props.value;

    // Button text
    if (props.buttonText) content.text = String(props.buttonText);

    // Input-specific content
    if (component.type === ComponentType.INPUT) {
      if (props.inputType) content.value = String(props.inputType);
    }

    return Object.keys(content).length > 0 ? content : undefined;
  }

  /**
   * Extract styling information from components
   */
  private extractStyling(component: Component): ComponentSummary['styling'] | undefined {
    const props = component.properties;
    const styling: ComponentSummary['styling'] = {};

    // Colors
    if (props.backgroundColor) styling.colors = [String(props.backgroundColor)];
    if (props.textColor) {
      if (!styling.colors) styling.colors = [];
      styling.colors.push(String(props.textColor));
    }
    if (props.borderColor) {
      if (!styling.colors) styling.colors = [];
      styling.colors.push(String(props.borderColor));
    }

    // Typography
    if (props.fontSize) styling.fontSize = String(props.fontSize);
    if (props.fontWeight) styling.fontWeight = String(props.fontWeight);

    // Theme
    if (props.theme) styling.theme = String(props.theme);

    return Object.keys(styling).length > 0 ? styling : undefined;
  }

  /**
   * Check if component type is visual (charts, etc.)
   */
  private isVisualComponent(type: ComponentType): boolean {
    return [
      ComponentType.CHART,
      ComponentType.BAR_CHART,
      ComponentType.LINE_CHART,
      ComponentType.PIE_CHART,
      ComponentType.SCATTER_CHART,
      ComponentType.AREA_CHART,
      ComponentType.HEATMAP_CHART,
      ComponentType.TREEMAP_CHART,
      ComponentType.GAUGE_CHART,
      ComponentType.TABLE,
      ComponentType.ADVANCED_TABLE
    ].includes(type);
  }

  /**
   * Sanitize component properties for AI consumption
   */
  private sanitizeProperties(properties: Record<string, unknown>): Record<string, unknown> {
    return sanitizeComponentPropertiesForAi(properties);
  }

  /**
   * Detect if project has agricultural context
   */
  private detectAgriculturalContext(text: string): boolean {
    const lowerText = text.toLowerCase();
    return AGRICULTURAL_KEYWORDS.some(keyword =>
      lowerText.includes(keyword)
    );
  }

  /**
   * Determine current workflow context
   */
  private summarizeVisualSeriesEntry(
    series: unknown,
  ): { name?: string; type?: string; data?: string } {
    if (!this.isRecord(series)) {
      return {};
    }

    return {
      name: series.name ? String(series.name) : undefined,
      type: series.type ? String(series.type) : undefined,
    };
  }

  private determineWorkflowContext(editorState: EditorStoreState): string {
    const components = editorState.components;
    const selectedCount = editorState.selectedComponents.length;
    const totalCount = components.length;

    if (totalCount === 0) {
      return "starting new project";
    } else if (selectedCount > 0) {
      return "editing selected components";
    } else if (totalCount < 5) {
      return "building initial layout";
    } else {
      return "refining existing design";
    }
  }

  /**
   * Get empty context for error cases
   */
  private getEmptyContext(): AIContextData {
    return {
      project: null,
      canvas: {
        selectedComponents: [],
        recentComponents: [],
        allComponents: [],
        viewport: Viewport.DESKTOP,
        totalComponents: 0,
        zoom: 1,
        gridEnabled: false,
        multiPageMode: false
      },
      user: {
        currentAction: "idle",
        lastActivity: new Date(),
        workflowContext: "unknown",
        isOwner: true,
        collaboratorCount: 0
      },
      timestamp: new Date(),
      contextVersion: "1.0.0"
    };
  }

  /**
   * Format context for AI prompt inclusion
   */
  formatContextForAI(context: AIContextData): string {
    const parts: string[] = [];

    // Project context
    if (context.project) {
      parts.push(`Current project: "${context.project.name}" - ${context.project.description}`);
      if (context.project.isAgricultural) {
        parts.push("This appears to be an agricultural/farming project.");
      }
    } else {
      parts.push("No project currently loaded.");
    }

    // Canvas context
    parts.push(`Canvas has ${context.canvas.totalComponents} total components.`);

    // Detailed selected component analysis
    if (context.canvas.selectedComponents.length > 0) {
      const selectedDetails = context.canvas.selectedComponents.map(c => {
        const details = [`${c.name} (${c.type})`];

        // Add content details
        if (c.content?.text) {
          details.push(`text: "${c.content.text}"`);
        }
        if (c.content?.title) {
          details.push(`title: "${c.content.title}"`);
        }

        // Add visual configuration details for charts
        if (c.visualConfig) {
          if (c.visualConfig.chartType) {
            details.push(`chart type: ${c.visualConfig.chartType}`);
          }
          if (c.visualConfig.axes?.xAxis?.label) {
            details.push(`X-axis: ${c.visualConfig.axes.xAxis.label}`);
          }
          if (c.visualConfig.axes?.yAxis?.label) {
            details.push(`Y-axis: ${c.visualConfig.axes.yAxis.label}`);
            if (c.visualConfig.axes.yAxis.unit) {
              details.push(`unit: ${c.visualConfig.axes.yAxis.unit}`);
            }
          }
          if (c.visualConfig.series && c.visualConfig.series.length > 0) {
            const seriesInfo = c.visualConfig.series
              .map(s => s.name || s.type)
              .filter(Boolean)
              .join(', ');
            if (seriesInfo) {
              details.push(`series: ${seriesInfo}`);
            }
          }
        }

        // Add styling information
        if (c.styling?.colors && c.styling.colors.length > 0) {
          details.push(`colors: ${c.styling.colors.join(', ')}`);
        }

        if (c.bindingContext) {
          details.push(this.formatBindingContextSummary(c.bindingContext));
        }

        return details.join(', ');
      });

      parts.push(`Currently selected: ${selectedDetails.join('; ')}`);
    }

    // Recent components with details
    if (context.canvas.recentComponents.length > 0) {
      const recentDetails = context.canvas.recentComponents.map(c => {
        let detail = `${c.name} (${c.type})`;
        if (c.content?.text) {
          detail += ` - "${c.content.text}"`;
        } else if (c.content?.title) {
          detail += ` - "${c.content.title}"`;
        }
        return detail;
      });
      parts.push(`Recently modified: ${recentDetails.join(', ')}`);
    }

    // User context
    parts.push(`User is currently ${context.user.currentAction} (${context.user.workflowContext}).`);

    if (context.user.collaboratorCount > 0) {
      parts.push(`${context.user.collaboratorCount} other users are collaborating.`);
    }

    return parts.join(" ");
  }

  private formatBindingContextSummary(summary: AiBindingContextSummary): string {
    const details = [
      `binding status: ${summary.status}`,
      `source: ${this.formatBindingSourceIdentity(summary.source)}`,
    ];

    const sourceLabel = this.formatBindingSourceLabels(summary);
    if (sourceLabel) {
      details.push(`labels: ${sourceLabel}`);
    }

    const fieldMappings = this.formatFieldMappings(summary.fieldMapping);
    if (fieldMappings) {
      details.push(`field mappings: ${fieldMappings}`);
    }

    details.push(`write target: ${this.formatWriteTarget(summary.writeTarget)}`);

    if (summary.relatedComponents.length > 0) {
      details.push(
        `related components: ${summary.relatedComponents
          .map((component) => `${component.name ?? component.id} (${component.type})`)
          .join(", ")}`,
      );
    }

    return details.join("; ");
  }

  private formatBindingSourceLabels(summary: AiBindingContextSummary): string | undefined {
    if (summary.sourceLabels) {
      const labelParts = [
        summary.sourceLabels.schemaDisplayName,
        summary.sourceLabels.schemaName,
        summary.sourceLabels.groupName,
        summary.sourceLabels.groupSlug,
      ].filter((value): value is string => Boolean(value));
      return labelParts.length > 0 ? labelParts.join(" / ") : undefined;
    }

    if (summary.viewLabels) {
      const labelParts = [
        summary.viewLabels.name,
        summary.viewLabels.ownerName,
        summary.viewLabels.viewOrganizationName,
      ].filter((value): value is string => Boolean(value));
      return labelParts.length > 0 ? labelParts.join(" / ") : undefined;
    }

    return undefined;
  }

  private formatFieldMappings(
    fieldMapping: Record<string, string | undefined>,
  ): string | undefined {
    const mappings = Object.entries(fieldMapping).flatMap(
      ([componentField, sourceField]) =>
        sourceField === undefined ? [] : [`${componentField}->${sourceField}`],
    );
    return mappings.length > 0 ? mappings.join(", ") : undefined;
  }

  private formatBindingSourceIdentity(
    source: AiBindingSourceIdentitySummary,
  ): string {
    if (source.kind === "unknown") {
      return "unknown";
    }

    if (source.kind === "static") {
      return `static adapter ${source.adapterId}`;
    }

    if (source.kind === "schema") {
      return [
        "schema",
        `ownerOrg=${source.dataOwnerOrganizationId}`,
        `owner=${source.ownerType}:${source.ownerId}`,
        `group=${source.groupId}`,
        `schema=${source.schemaId}`,
      ].join(" ");
    }

    return [
      source.kind,
      `owner=${source.ownerType}:${source.ownerId}`,
      `viewOrg=${source.viewOrganizationId}`,
      `view=${source.viewId}`,
    ].join(" ");
  }

  private formatWriteTarget(summary: AiBindingWriteTargetSummary): string {
    if (isFederatedWriteTargetSummary(summary)) {
      const readiness = this.formatWriteReadiness(summary.readiness);
      return [
        `present federated source ${summary.targetSourceId}`,
        `view ${this.formatBindingSourceIdentity(summary.view)}`,
        `target ${this.formatBindingSourceIdentity(summary.source)}`,
        readiness,
      ].filter((value): value is string => Boolean(value)).join("; ");
    }

    if (isDirectWriteTargetSummary(summary)) {
      const readiness = this.formatWriteReadiness(summary.readiness);
      return [
        `present ${this.formatBindingSourceIdentity(summary.target)}`,
        readiness,
      ].filter((value): value is string => Boolean(value)).join("; ");
    }

    return "not present";
  }

  private formatWriteReadiness(
    readiness: AiBindingWriteReadiness,
  ): string | undefined {
    if (readiness === undefined) {
      return undefined;
    }

    const entries = Object.entries(readiness).map(
      ([operation, state]) => `${operation}=${state}`,
    );
    return entries.length > 0 ? `readiness ${entries.join(", ")}` : undefined;
  }

  // ============================================================================
  // PHASE 1.1: Enhanced Context Collection Methods
  // ============================================================================

  /**
   * Collect enhanced context for a specific component
   * Includes parent, siblings, viewport, and layer information
   */
  collectEnhancedComponentContext(
    component: Component,
    options: ContextCollectionOptions = {}
  ): EnhancedComponentContext {
    const {
      includeParent = true,
      includeSiblings = true,
      includeViewport = true,
      includeLayering = true,
      maxSiblings = 5
    } = options;

    const editorState = useEditorStore.getState();
    const props: BaseComponentProperties = component.properties;
    const safeComponentContext = buildSafeAiComponentContextFromComponent(
      component,
      editorState.components,
    );

    // Build enhanced context
    const enhancedContext: EnhancedComponentContext = {
      component: {
        id: component.id,
        type: component.type,
        name: this.getComponentDisplayName(component),
        properties: safeComponentContext.properties,
        bindingContext: safeComponentContext.bindingContext,
        style: component.style,
        size: {
          width: typeof component.size?.width === 'number' ? component.size.width : 200,
          height: typeof component.size?.height === 'number' ? component.size.height : 100
        },
        position: {
          x: component.position?.x ?? 0,
          y: component.position?.y ?? 0,
          z: component.position?.z ?? 0
        },
        semanticRole: props._semanticRole,
        templateInstanceId: props._templateInstanceId
      },
      viewport: includeViewport
        ? this.collectViewportContext()
        : this.getDefaultViewportContext(),
      layering: includeLayering
        ? this.collectLayerContext(component, editorState.components)
        : this.getDefaultLayerContext()
    };

    // Collect parent context if component has a parent
    if (includeParent && component.parentId) {
      const parentContext = this.collectParentContext(component, editorState);
      if (parentContext) {
        enhancedContext.parent = parentContext;
      }
    }

    // Collect sibling context
    if (includeSiblings && component.parentId) {
      const siblings = this.collectSiblingContext(component, editorState, maxSiblings);
      if (siblings.length > 0) {
        enhancedContext.siblings = siblings;
      }
    }

    return enhancedContext;
  }

  /**
   * Collect parent container context
   */
  private collectParentContext(
    component: Component,
    editorState: EditorStoreState,
  ): ParentContext | undefined {
    if (!component.parentId) {
      return undefined;
    }

    const parent = editorState.components.find((c: Component) => c.id === component.parentId);
    if (!parent) {
      return undefined;
    }

    const parentProps = parent.properties;
    const parentStyle = parent.style;
    const parentChildren = parent.children;
    const getFlexDirectionValue = (
      value: unknown
    ): ParentContext['layout']['flexDirection'] | undefined =>
      value === 'row' || value === 'column' || value === 'row-reverse' || value === 'column-reverse'
        ? value
        : undefined;
    const getStringValue = (value: unknown): string | undefined =>
      typeof value === 'string' ? value : undefined;
    const getStringOrNumberValue = (value: unknown): string | number | undefined =>
      typeof value === 'string' || typeof value === 'number' ? value : undefined;

    // Calculate child index (position among siblings)
    const childIndex = parentChildren.indexOf(component.id);

    // Determine layout type from parent
    let display: 'flex' | 'grid' | 'block' = 'block';
    if (parent.type === ComponentType.FLEX_CONTAINER ||
        parentProps?.display === 'flex' ||
        parentStyle?.display === 'flex') {
      display = 'flex';
    } else if (parentProps?.display === 'grid' || parentStyle?.display === 'grid') {
      display = 'grid';
    }

    // Extract layout properties
    const layout: ParentContext['layout'] = {
      display,
      flexDirection: getFlexDirectionValue(parentProps.flexDirection)
        ?? getFlexDirectionValue(parentStyle.flexDirection)
        ?? 'row',
      justifyContent: getStringValue(parentProps.justifyContent) ?? getStringValue(parentStyle.justifyContent),
      alignItems: getStringValue(parentProps.alignItems) ?? getStringValue(parentStyle.alignItems),
      gap: getStringOrNumberValue(parentProps.gap) ?? getStringOrNumberValue(parentStyle.gap),
      padding: getStringOrNumberValue(parentProps.padding) ?? getStringOrNumberValue(parentStyle.padding),
    };

    const parentContext: ParentContext = {
      id: parent.id,
      type: parent.type,
      name: this.getComponentDisplayName(parent),
      layout,
      dimensions: {
        width: typeof parent.size.width === 'number' ? parent.size.width : 400,
        height: typeof parent.size.height === 'number' ? parent.size.height : 300
      },
      childCount: parentChildren.length,
      childIndex: childIndex >= 0 ? childIndex : 0
    };

    return parentContext;
  }

  /**
   * Collect sibling components context
   * Returns up to maxSiblings nearest siblings (before and after)
   */
  private collectSiblingContext(
    component: Component,
    editorState: EditorStoreState,
    maxSiblings: number = 5
  ): SiblingContext[] {
    if (!component.parentId) {
      return [];
    }

    const parent = editorState.components.find((c: Component) => c.id === component.parentId);
    if (!parent) {
      return [];
    }

    const siblings: SiblingContext[] = [];
    const childIds = parent.children;
    const componentIndex = childIds.indexOf(component.id);

    if (componentIndex === -1) {
      return [];
    }

    // Collect siblings before and after
    for (let i = 0; i < childIds.length && siblings.length < maxSiblings; i++) {
      if (i === componentIndex) continue; // Skip the component itself

      const siblingId = childIds[i];
      const sibling = editorState.components.find((c: Component) => c.id === siblingId);
      if (!sibling) continue;

      // Calculate distance from component
      const dx = (sibling.position?.x ?? 0) - (component.position?.x ?? 0);
      const dy = (sibling.position?.y ?? 0) - (component.position?.y ?? 0);
      const distance = Math.sqrt(dx * dx + dy * dy);

      const siblingContext: SiblingContext = {
        id: sibling.id,
        type: sibling.type,
        name: this.getComponentDisplayName(sibling),
        size: {
          width: typeof sibling.size.width === 'number' ? sibling.size.width : 200,
          height: typeof sibling.size.height === 'number' ? sibling.size.height : 100
        },
        position: {
          x: sibling.position?.x ?? 0,
          y: sibling.position?.y ?? 0
        },
        relationship: i < componentIndex ? 'before' : 'after',
        distanceFromComponent: Math.round(distance)
      };

      siblings.push(siblingContext);
    }

    // Sort by distance and limit to maxSiblings
    return siblings
      .sort((a, b) => a.distanceFromComponent - b.distanceFromComponent)
      .slice(0, maxSiblings);
  }

  /**
   * Collect current viewport context
   */
  collectViewportContext(): ViewportContext {
    try {
      const responsiveState = useResponsiveStore.getState();
      const activeViewportId = responsiveState.activeViewport;

      // Find the active viewport breakpoint
      const allViewports = [
        ...responsiveState.availableViewports,
        ...responsiveState.customViewports
      ];
      const activeViewport = allViewports.find(v => v.id === activeViewportId);

      // Determine breakpoint category
      const viewportWidth = activeViewport?.width || 1440;
      let currentBreakpoint: 'mobile' | 'tablet' | 'desktop' = 'desktop';
      if (viewportWidth < 600) {
        currentBreakpoint = 'mobile';
      } else if (viewportWidth < 1024) {
        currentBreakpoint = 'tablet';
      }

      // Get scales
      const userZoom = responsiveState.userZoom || 1;
      const viewportScale = responsiveState.viewportScales?.[activeViewportId] || 1;

      const viewportContext: ViewportContext = {
        currentBreakpoint,
        viewportWidth,
        viewportHeight: activeViewport?.height || 900,
        viewportName: activeViewport?.name || 'Desktop',
        zoom: userZoom * viewportScale,
        userZoom,
        viewportScale
      };

      return viewportContext;

    } catch {
      logger.warn("Error collecting viewport context, using defaults");
      return this.getDefaultViewportContext();
    }
  }

  /**
   * Collect layer/z-index context for component
   */
  private collectLayerContext(component: Component, allComponents: Component[]): LayerContext {
    const componentZ = component.position?.z || 0;
    const componentBounds = this.getComponentBounds(component);

    const componentsAbove: string[] = [];
    const componentsBelow: string[] = [];
    const overlappingComponents: string[] = [];

    for (const other of allComponents) {
      if (other.id === component.id) continue;

      const otherZ = other.position?.z || 0;
      const otherBounds = this.getComponentBounds(other);

      // Check z-index relationship
      if (otherZ > componentZ) {
        componentsAbove.push(other.id);
      } else if (otherZ < componentZ) {
        componentsBelow.push(other.id);
      }

      // Check for overlap
      if (this.boundsOverlap(componentBounds, otherBounds)) {
        overlappingComponents.push(other.id);
      }
    }

    const layerContext: LayerContext = {
      zIndex: componentZ,
      componentsAbove,
      componentsBelow,
      isOverlapping: overlappingComponents.length > 0,
      overlappingComponents
    };

    if (overlappingComponents.length > 0) {
      logger.debug("AI context layer overlaps detected", {
        zIndex: componentZ,
        overlappingCount: overlappingComponents.length
      });
    }

    return layerContext;
  }

  /**
   * Get component bounds for overlap detection
   */
  private getComponentBounds(component: Component): { left: number; right: number; top: number; bottom: number } {
    const width = typeof component.size.width === 'number' ? component.size.width : 200;
    const height = typeof component.size.height === 'number' ? component.size.height : 100;

    const x = component.position?.x ?? 0;
    const y = component.position?.y ?? 0;

    return {
      left: x,
      right: x + width,
      top: y,
      bottom: y + height
    };
  }

  /**
   * Check if two bounds overlap
   */
  private boundsOverlap(
    a: { left: number; right: number; top: number; bottom: number },
    b: { left: number; right: number; top: number; bottom: number }
  ): boolean {
    return !(a.right < b.left || a.left > b.right || a.bottom < b.top || a.top > b.bottom);
  }

  /**
   * Get default viewport context for error cases
   */
  private getDefaultViewportContext(): ViewportContext {
    return {
      currentBreakpoint: 'desktop',
      viewportWidth: 1440,
      viewportHeight: 900,
      viewportName: 'Desktop',
      zoom: 1,
      userZoom: 1,
      viewportScale: 1
    };
  }

  /**
   * Get default layer context for error cases
   */
  private getDefaultLayerContext(): LayerContext {
    return {
      zIndex: 0,
      componentsAbove: [],
      componentsBelow: [],
      isOverlapping: false,
      overlappingComponents: []
    };
  }

  /**
   * Compress enhanced context for token efficiency
   * Only includes properties relevant to common AI operations
   */
  compressContext(
    context: EnhancedComponentContext,
    command?: string
  ): CompressedComponentContext {
    // Determine relevant properties based on command
    const relevantProperties = this.extractRelevantProperties(
      context.component.properties,
      context.component.style,
      command
    );

    const compressed: CompressedComponentContext = {
      component: {
        id: context.component.id,
        type: String(context.component.type),
        name: context.component.name,
        relevantProperties,
        size: context.component.size,
        position: {
          x: context.component.position.x,
          y: context.component.position.y
        }
      },
      viewport: {
        breakpoint: context.viewport.currentBreakpoint,
        width: context.viewport.viewportWidth,
        height: context.viewport.viewportHeight
      }
    };

    // Include compressed parent info if available
    if (context.parent) {
      compressed.parent = {
        type: String(context.parent.type),
        layout: context.parent.layout.display,
        dimensions: context.parent.dimensions,
        childIndex: context.parent.childIndex,
        childCount: context.parent.childCount
      };
    }

    // Include compressed sibling info if available
    if (context.siblings && context.siblings.length > 0) {
      compressed.siblings = context.siblings.slice(0, 3).map(s => ({
        name: s.name,
        type: String(s.type),
        size: s.size,
        relationship: s.relationship
      }));
    }

    // Include layer info only if there are overlaps
    if (context.layering.isOverlapping) {
      compressed.layer = {
        zIndex: context.layering.zIndex,
        hasOverlaps: true
      };
    }

    return compressed;
  }

  /**
   * Extract only properties relevant to the command
   */
  private extractRelevantProperties(
    properties: Record<string, unknown>,
    style: Record<string, unknown>,
    command?: string
  ): Record<string, unknown> {
    const relevant: Record<string, unknown> = {};
    const lowerCommand = (command || '').toLowerCase();

    // Typography-related commands
    if (/font|text|size|bold|italic|h[1-6]/i.test(lowerCommand)) {
      if (properties.fontSize) relevant.fontSize = properties.fontSize;
      if (properties.fontWeight) relevant.fontWeight = properties.fontWeight;
      if (properties.fontStyle) relevant.fontStyle = properties.fontStyle;
      if (properties.textAlign) relevant.textAlign = properties.textAlign;
      if (properties.content) relevant.content = properties.content;
    }

    // Color-related commands
    if (/color|background|theme/i.test(lowerCommand)) {
      if (style.color) relevant.color = style.color;
      if (style.backgroundColor) relevant.backgroundColor = style.backgroundColor;
      if (properties.color) relevant.textColor = properties.color;
    }

    // Size/position-related commands
    if (/width|height|size|center|position|align|margin|padding/i.test(lowerCommand)) {
      if (style.width) relevant.styleWidth = style.width;
      if (style.height) relevant.styleHeight = style.height;
      if (style.margin) relevant.margin = style.margin;
      if (style.padding) relevant.padding = style.padding;
    }

    // Layout-related commands
    if (/flex|layout|direction|justify|align/i.test(lowerCommand)) {
      if (properties.flexDirection) relevant.flexDirection = properties.flexDirection;
      if (properties.justifyContent) relevant.justifyContent = properties.justifyContent;
      if (properties.alignItems) relevant.alignItems = properties.alignItems;
      if (properties.gap) relevant.gap = properties.gap;
    }

    // If no specific command or no matches, include common properties
    if (Object.keys(relevant).length === 0) {
      const commonProps = ['fontSize', 'fontWeight', 'textAlign', 'content', 'color', 'backgroundColor'];
      for (const prop of commonProps) {
        if (properties[prop] !== undefined) relevant[prop] = properties[prop];
        if (style[prop] !== undefined) relevant[prop] = style[prop];
      }
    }

    return relevant;
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value);
  }

  /**
   * Format enhanced context for AI prompt inclusion
   */
  formatEnhancedContextForAI(context: EnhancedComponentContext): string {
    const parts: string[] = [];

    // Component info
    parts.push(`Component: ${context.component.name} (${context.component.type})`);
    parts.push(`Size: ${context.component.size.width}x${context.component.size.height}px`);
    parts.push(`Position: (${context.component.position.x}, ${context.component.position.y})`);
    if (context.component.bindingContext) {
      parts.push("Binding: " + this.formatBindingContextSummary(context.component.bindingContext));
    }

    // Parent info
    if (context.parent) {
      parts.push(`Parent: ${context.parent.name} (${context.parent.type})`);
      parts.push(`Parent layout: ${context.parent.layout.display}`);
      if (context.parent.layout.flexDirection) {
        parts.push(`Flex direction: ${context.parent.layout.flexDirection}`);
      }
      parts.push(`Child position: ${context.parent.childIndex + 1} of ${context.parent.childCount}`);
    }

    // Siblings info
    if (context.siblings && context.siblings.length > 0) {
      const siblingsBefore = context.siblings.filter(s => s.relationship === 'before');
      const siblingsAfter = context.siblings.filter(s => s.relationship === 'after');
      if (siblingsBefore.length > 0) {
        parts.push(`Siblings before: ${siblingsBefore.map(s => `${s.name} (${s.size.width}x${s.size.height})`).join(', ')}`);
      }
      if (siblingsAfter.length > 0) {
        parts.push(`Siblings after: ${siblingsAfter.map(s => `${s.name} (${s.size.width}x${s.size.height})`).join(', ')}`);
      }
    }

    // Viewport info
    parts.push(`Viewport: ${context.viewport.viewportName} (${context.viewport.viewportWidth}x${context.viewport.viewportHeight})`);
    parts.push(`Breakpoint: ${context.viewport.currentBreakpoint}`);

    // Layer info
    if (context.layering.isOverlapping) {
      parts.push(`Z-Index: ${context.layering.zIndex} (overlapping with ${context.layering.overlappingComponents.length} components)`);
    }

    return parts.join('\n');
  }
}

// Export singleton instance
export const aiContextService = AIContextService.getInstance();

// Re-export types for convenience
export type {
  EnhancedComponentContext,
  ParentContext,
  SiblingContext,
  ViewportContext,
  LayerContext,
  ContextCollectionOptions,
  CompressedComponentContext
} from "@/types/ai";
